import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Divider,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { APP_ROOT_PATH } from '@/config/paths';
import { authService, generateOTP } from '@/services/authService';
import { hasRequiredRole } from '@/services/authRoles';
import PhoneStep from './PhoneStep';
import MethodStep from './MethodStep';
import OtpStep from './OtpStep';
import PasswordLogin from './PasswordLogin';
import SetNewPasswordStep from './SetNewPasswordStep';
import { phoneSchema, type LoginWithPasswordValues, type SetNewPasswordValues } from './zod';

type Step =
  | 'phone'           // 1. enter + verify phone number
  | 'method'          // 2. choose OTP or password login
  | 'otp'             // 3a. OTP login
  | 'password'        // 3b. password login
  | 'forgotOtp'       // 4b. forgot password — OTP verification (+ captcha)
  | 'forgotReset'     // 4c. forgot password — set new password
  | 'forgotDone';      // 4d. forgot password — success

const RESEND_COOLDOWN_SECS = 60;
const ISD_CODE = '+91';

export default function LoginPage(): JSX.Element {
  const theme = useTheme();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false); // silent verify on 10-digit input
  const [verifyFailed, setVerifyFailed] = useState(false); // true when verifyPhoneNumber API errors
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECS);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const recaptchaResolver = useRef<((token: string) => void) | null>(null);
  const recaptchaWidgetId = useRef<number | null>(null);

  useEffect(() => {
    const initRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.render) {
        if (recaptchaWidgetId.current === null) {
          try {
            recaptchaWidgetId.current = window.grecaptcha.render('recaptcha-container', {
              sitekey: import.meta.env.RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI', // Test key
              size: 'invisible',
              callback: (token: string) => {
                if (recaptchaResolver.current) {
                  recaptchaResolver.current(token);
                  recaptchaResolver.current = null;
                }
              },
              'error-callback': () => {
                setError('reCAPTCHA failed. Please try again.');
                if (recaptchaResolver.current) {
                  recaptchaResolver.current('');
                  recaptchaResolver.current = null;
                }
              }
            });
          } catch (e) {
          }
        }
      } else {
        setTimeout(initRecaptcha, 200);
      }
    };
    initRecaptcha();
  }, []);

  const getCaptchaToken = (): Promise<string> => {
    return new Promise((resolve) => {
      recaptchaResolver.current = resolve;
      if (recaptchaWidgetId.current !== null) {
        window.grecaptcha.execute(recaptchaWidgetId.current);
      } else {
        resolve(''); // Fallback if not loaded
      }
    });
  };

  const resetCaptcha = () => {
    if (recaptchaWidgetId.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(recaptchaWidgetId.current);
    }
  };

  // --- Step 1: phone number entry + silent verification ---
  const handlePhoneChange = async (val: string) => {
    const digitsOnly = val.replace(/\D/g, '').slice(0, 10);
    setPhone(digitsOnly);
    setError('');
    setVerifyFailed(false);
    setPhoneVerified(false);
    if (digitsOnly.length === 10 && phoneSchema.safeParse({ phone: digitsOnly }).success) {
      setVerifying(true);
      try {
        const res = await authService.verifyPhoneNumber({ dialCode: ISD_CODE, number: digitsOnly });
        if (!res?.data?.existingUser) {
          setError('Phone number not found. Please check and try again.');
        } else {
          setPhoneVerified(true);
        }
      } catch {
        setVerifyFailed(true);
        setError('Phone number not found. Please check and try again.');
      } finally {
        setVerifying(false);
      }
    }
  };

  const handleContinueFromPhone = () => {
    setError('');
    const result = phoneSchema.safeParse({ phone });
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? 'Please enter a valid 10-digit phone number.');
      return;
    }
    if (!phoneVerified) {
      setError('Phone number not found. Please check and try again.');
      return;
    }
    setStep('method');
  };

  // --- Step 2: method selection ---
  const handleSelectOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const captchaToken = await getCaptchaToken();
      if (!captchaToken) throw new Error('Captcha failed');

      const requestPayload: generateOTP = {
        admission_number: phone,
        captcha_token: captchaToken,
        isd_code: ISD_CODE,
        phone: phone,
        product_id: 1000,
        tenant_id: 1
      };
      await authService.requestotp(requestPayload);
      setStep('otp');
      startCooldown();
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleSelectPassword = () => {
    setError('');
    setStep('password');
  };

  // --- Step 3a: OTP login ---
  const handleVerifyOtp = async (otp: string) => {
    if (permissionDenied) return;
    setError('');
    setLoading(true);
    try {
      const captchaToken = await getCaptchaToken();
      if (!captchaToken) throw new Error('Captcha failed');
      const requestPayload: any = {
        admission_number: phone,
        isd_code: ISD_CODE,
        phone: phone,
        product_id: 1000,
        tenant_id: 1,
        otp: otp
      };
      const data=await authService.verifyOtp(requestPayload);
      const accessToken = data?.data?.accessToken;
      if (!accessToken) {
        setError('Verification failed. Please try again.');
        return;
      }
      localStorage.setItem('TOKEN', accessToken);
      const res=await authService.getLoginDto();

      if (!hasRequiredRole(res?.data[0]?.roles)) {
        localStorage.removeItem('TOKEN');
        localStorage.removeItem('user_details_from_server');
        setPermissionDenied(true);
        setError('You do not have permission to access this dashboard.');
        return;
      }

      localStorage.setItem('user_details_from_server', JSON.stringify(res?.data));
      navigate(APP_ROOT_PATH, { replace: true });
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || permissionDenied) return;
    setError('');
    setLoading(true);
    try {
      const captchaToken = await getCaptchaToken();
      if (!captchaToken) throw new Error('Captcha failed');

      const requestPayload: generateOTP = {
        admission_number: phone,
        captcha_token: captchaToken,
        isd_code: ISD_CODE,
        phone: phone,
        product_id: 1000,
        tenant_id: 1
      };
      await authService.requestotp(requestPayload);
      startCooldown();
    } catch {
      setError('Failed to resend OTP.');
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  // --- Step 3b: password login ---
  const handleLoginWithPassword = async (values: LoginWithPasswordValues) => {
    setError('');
    setLoading(true);
    try {
      const reqPayload={
        isd_code :"+91",
        password: values.password,
        phone: phone
      }
      const data = await authService.loginWithPassword(reqPayload);
      const accessToken = data?.data?.accessToken;
      if (!accessToken) {
        setError('Invalid password.');
        return;
      }
      localStorage.setItem('TOKEN', accessToken);
      const res = await authService.getLoginDto();

      if (!hasRequiredRole(res?.data[0]?.roles)) {
        localStorage.removeItem('TOKEN');
        localStorage.removeItem('user_details_from_server');
        setPermissionDenied(true);
        setError('You do not have permission to access this dashboard.');
        return;
      }

      localStorage.setItem('user_details_from_server', JSON.stringify(res?.data));
      navigate(APP_ROOT_PATH, { replace: true });
    } catch {
      setError('Invalid password.');
    } finally {
      setLoading(false);
    }
  };
  
  // --- Step 4a: forgot password — request OTP, then show the OTP step ---
  const handleForgotPassword = async () => {
    setError('');
    setLoading(true);
    try {
      const captchaToken = await getCaptchaToken();
      if (!captchaToken) throw new Error('Captcha failed');

      const requestPayload: generateOTP = {
        admission_number: phone,
        captcha_token: captchaToken,
        isd_code: ISD_CODE,
        phone: phone,
        product_id: 1000,
        tenant_id: 1,
      };
      await authService.requestotp(requestPayload);
      setStep('forgotOtp');
      startCooldown();
    } catch {
      setError('Failed to send OTP. Please try again.');
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  // --- Step 4b: forgot password — verify OTP, then move to set-new-password ---
  const handleVerifyForgotPasswordOtp = async (otp: string) => {
    setError('');
    setLoading(true);
    try {
      const captchaToken = await getCaptchaToken();
      if (!captchaToken) throw new Error('Captcha failed');
      const requestPayload: any = {
        admission_number: phone,
        isd_code: ISD_CODE,
        phone: phone,
        product_id: 1000,
        tenant_id: 1,
        otp: otp,
      };
      const data = await authService.verifyOtp(requestPayload);
      const accessToken = data?.data?.accessToken;
      if (!accessToken) {
        setError('Verification failed. Please try again.');
        return;
      }
      // Token identifies the user for the subsequent password reset call,
      // but we intentionally do NOT navigate to the dashboard here.
      localStorage.setItem('TOKEN', accessToken);
      setStep('forgotReset');
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (values: SetNewPasswordValues) => {
    setError('');
    setLoading(true);
    try {
      // API integration pending — submit the new password with the reset token.
      const reqPayload={
        password:values.password
      }
      await authService.resetPassword(reqPayload);
      void values;

      setStep('forgotDone');
    } catch {
      setError('Could not reset your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // --- Back navigation ---
  const handleBack = () => {
    setError('');
    setPermissionDenied(false);
    if (step === 'method') setStep('phone');
    else if (step === 'otp' || step === 'password') setStep('method');
    else if (step === 'forgotOtp') setStep('password');
    else if (step === 'forgotReset') setStep('forgotOtp');
  };

  const showBackLink = step === 'method' || step === 'otp' || step === 'password' || step === 'forgotOtp' || step === 'forgotReset';

  const backLabel = (() => {
    switch (step) {
      case 'method': return 'Change number';
      case 'otp': return 'Change method';
      case 'password': return 'Change method';
      case 'forgotOtp': return 'Back to sign in';
      case 'forgotReset': return 'Back to OTP';
      default: return 'Back';
    }
  })();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>

      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 480px',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(160deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
          p: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative rings */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              borderRadius: '50%',
              border: `1px solid ${alpha('#fff', 0.15)}`,
              width: 200 + i * 160,
              height: 200 + i * 160,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
          />
        ))}

        <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '20px',
              backgroundColor: alpha('#fff', 0.15),
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4,
              boxShadow: `0 8px 32px ${alpha('#000', 0.2)}`,
            }}
          >
            <SchoolOutlinedIcon sx={{ fontSize: 40, color: '#fff' }} />
          </Box>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, mb: 1, lineHeight: 1.2 }}>
          Genius Live Insights Dashboard
          </Typography>
          <Typography sx={{ color: alpha('#fff', 0.75), fontSize: '0.9375rem' }}>
            Operations Portal
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
          p: { xs: 2, sm: 4 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 440 }}>

          {/* Mobile logo */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.5, mb: 4 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.125rem' }}>A</Typography>
            </Box>
            <Typography variant="h5" fontWeight={700}>Acad Operations</Typography>
          </Box>

          {/* Back navigation */}
          {showBackLink && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 3,
                cursor: 'pointer',
                color: 'primary.main',
                width: 'fit-content',
              }}
              onClick={handleBack}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {backLabel}
              </Typography>
            </Box>
          )}

          {/* Render active step */}
          {step === 'phone' && (
            <PhoneStep
              phone={phone}
              onChange={handlePhoneChange}
              onSubmit={handleContinueFromPhone}
              error={error}
              loading={loading || verifying}
              verifyFailed={verifyFailed}
              submitLabel="Continue"
            />
          )}

          {step === 'method' && (
            <MethodStep
              phone={phone}
              onSelectOtp={handleSelectOtp}
              onSelectPassword={handleSelectPassword}
            />
          )}

          {step === 'otp' && (
            <OtpStep
              phone={phone}
              onVerify={handleVerifyOtp}
              onResend={handleResend}
              error={error}
              loading={loading}
              resendCooldown={cooldown}
              permissionDenied={permissionDenied}
            />
          )}

          {step === 'password' && (
            <PasswordLogin
              onSubmit={handleLoginWithPassword}
              onForgotPassword={handleForgotPassword}
              error={error}
              loading={loading}
            />
          )}

          {step === 'forgotOtp' && (
            <OtpStep
              phone={phone}
              onVerify={handleVerifyForgotPasswordOtp}
              onResend={handleForgotPassword}
              error={error}
              loading={loading}
              resendCooldown={cooldown}
            />
          )}

          {step === 'forgotReset' && (
            <SetNewPasswordStep
              onSubmit={handleSetNewPassword}
              error={error}
              loading={loading}
            />
          )}

          {step === 'forgotDone' && (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 28 }} />
              </Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                Password reset
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Your password has been changed successfully. You can now sign in with your new password.
              </Typography>
              <Typography
                component="button"
                type="button"
                onClick={() => {
                  setError('');
                  setStep('password');
                }}
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  textDecoration: 'none',
                  background: 'none',
                  border: 'none',
                  p: 0,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Back to sign in
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 3 }}>
            <Typography variant="caption" color="text.disabled">
              Genius Live Insights Dashboard
            </Typography>
          </Divider>

          <div id="recaptcha-container"></div>
        </Box>
      </Box>
    </Box>
  );
}
